import { Reveal } from "@/components/Reveal";
import { TerrainHorizon } from "@/components/OrganicLines";
import { CallToAction } from "@/components/sections/CallToAction";
import { TAGLINE } from "@/lib/site";

const CRAFT_OUTCOMES = [
  {
    role: "The trainer",
    outcome: "trains more, sharper",
    detail: "Care, workload and results stay attached to the horse, not buried in somebody's phone.",
    proof: "Ask aloud: “When did James last see the farrier?”",
  },
  {
    role: "The breeder",
    outcome: "researches deeper",
    detail: "Lineage, photos, awards and outcomes remain connected in one horse record.",
    proof: "Compare the history before planning the next match.",
  },
  {
    role: "The owner",
    outcome: "scales the craft",
    detail: "The team can ask, update and follow up without waiting behind one record-keeper.",
    proof: "Coming next: create and approve an invoice in the same conversation.",
  },
] as const;

const MOBILE_CAPABILITIES = [
  ["Talk", "Ask from the aisle without stopping to type."],
  ["Listen", "Hear the answer when your hands are busy."],
  ["Choose", "Use BarnAssist, Nutrition, or Health & Vet."],
] as const;

const INVOICE_STEPS = [
  ["01", "You ask"],
  ["02", "Carrot drafts"],
  ["03", "You approve"],
  ["04", "Stripe sends"],
] as const;

/**
 * Section 5 of the descent: the harvest. Back above ground, the recovered
 * memory turns into more time for the craft, a hands-free mobile assistant,
 * and user-controlled actions such as the upcoming Stripe invoice flow.
 */
export function Harvest() {
  return (
    <section id="payoff" className="harvest-field relative overflow-hidden">
      <TerrainHorizon className="pointer-events-none absolute inset-x-0 top-0 h-[5.25rem] w-full" />
      <div className="relative mx-auto max-w-7xl px-5 pb-4 pt-24 sm:px-8 sm:pt-28">
        <Reveal className="harvest-intro max-w-3xl">
          <p className="text-[0.68rem] font-bold uppercase tracking-[0.24em] text-carrot-ink">Back above ground</p>
          {/* the tagline rises out of the soil band above: the "resurfacing"
              beat of the descent narrative, played once on reveal */}
          <div className="tagline-mask mt-5">
            <h2 className="tagline-rise pb-1 font-serif text-4xl font-semibold italic leading-[1.12] text-field-deep sm:text-5xl">
              “{TAGLINE}.”
            </h2>
          </div>
          <p className="mt-6 max-w-2xl text-lg leading-relaxed text-ink/70">
            Once the memory is out of heads and group texts, it can answer from the aisle, surface what needs
            attention, and prepare the next action. The horseman stays in control; the overhead stops setting
            the pace.
          </p>
        </Reveal>

        <div className="craft-outcomes mt-14 border-y border-ink/10">
          {CRAFT_OUTCOMES.map((item, i) => (
            <Reveal key={item.role} delay={i * 90}>
              <article
                className={`grid gap-4 py-7 md:grid-cols-[0.22fr_0.28fr_0.5fr] md:items-start md:gap-8 md:py-8 ${
                  i ? "border-t border-ink/10" : ""
                }`}
              >
                <p className="pt-1 text-xs font-bold uppercase tracking-[0.2em] text-carrot-ink">{item.role}</p>
                <h3 className="text-2xl font-extrabold leading-[1.05] text-field-deep sm:text-3xl">{item.outcome}</h3>
                <div className="md:pl-6">
                  <p className="max-w-md text-sm leading-relaxed text-ink/70">{item.detail}</p>
                  <p className="mt-3 max-w-lg font-serif text-base italic leading-relaxed text-field-deep/85">
                    {item.proof}
                  </p>
                </div>
              </article>
            </Reveal>
          ))}
        </div>

        {/* Real mobile product proof: direct voice input, spoken output, and specialist helpers. */}
        <Reveal delay={120} className="mt-20 sm:mt-24">
          <section className="mobile-proof overflow-hidden rounded-[2.25rem] bg-field-deep text-cream">
            <div className="grid items-center gap-12 px-6 py-12 sm:px-10 sm:py-16 lg:grid-cols-[0.8fr_1.2fr] lg:px-16 lg:py-20">
              <div className="max-w-xl">
                <p className="text-[0.68rem] font-bold uppercase tracking-[0.24em] text-carrot-bright">Mobile · available now</p>
                <h3 className="mt-5 text-4xl font-extrabold leading-[0.98] tracking-[-0.035em] text-cream sm:text-5xl">
                  The barn doesn't stop for a keyboard.
                </h3>
                <p className="mt-6 text-lg leading-relaxed text-cream/72">
                  Open BarnAssist, talk naturally, and listen to the answer. The same assistant can search
                  horses, care activity, feeding, health and finances while you stay with the work.
                </p>

                <ol className="mt-9 border-y border-cream/12">
                  {MOBILE_CAPABILITIES.map(([name, detail], i) => (
                    <li key={name} className={`grid grid-cols-[4.5rem_1fr] gap-4 py-4 ${i ? "border-t border-cream/12" : ""}`}>
                      <span className="font-display font-bold text-carrot-bright">{name}</span>
                      <span className="text-sm leading-relaxed text-cream/65">{detail}</span>
                    </li>
                  ))}
                </ol>
              </div>

              <div className="phone-stage relative mx-auto h-[34rem] w-full max-w-[38rem] sm:h-[39rem]">
                <figure className="phone-shell phone-shell-back absolute left-0 top-10 w-[47%] max-w-[16rem] -rotate-[5deg]">
                  <div className="phone-screen aspect-[9/18.8]">
                    <img
                      src="/static/mobile-helpers.webp"
                      alt="CarrotOnTheHorse mobile app showing BarnAssist, Nutrition, and Health & Vet helpers"
                      width={820}
                      height={1781}
                      loading="lazy"
                      decoding="async"
                      className="size-full object-cover object-top"
                    />
                  </div>
                  <figcaption>Choose the right helper</figcaption>
                </figure>

                <figure className="phone-shell phone-shell-front absolute right-0 top-0 w-[51%] max-w-[17.5rem] rotate-[3deg]">
                  <div className="phone-screen aspect-[9/15.4]">
                    <img
                      src="/static/mobile-voice.webp"
                      alt="CarrotOnTheHorse BarnAssist answering a spoken question about a horse's farrier history"
                      width={820}
                      height={1781}
                      loading="lazy"
                      decoding="async"
                      className="size-full object-cover object-top"
                    />
                  </div>
                  <figcaption>Speak in. Listen back.</figcaption>
                </figure>
              </div>
            </div>
          </section>
        </Reveal>

        {/* Roadmap item: Carrot drafts; the barn reviews; Stripe moves the money. */}
        <Reveal delay={140} className="mt-8 sm:mt-10">
          <section className="invoice-proof overflow-hidden rounded-[2.25rem] bg-carrot-soft text-ink">
            <div className="grid gap-12 px-6 py-12 sm:px-10 sm:py-16 lg:grid-cols-[0.82fr_1.18fr] lg:items-center lg:px-16 lg:py-20">
              <div className="max-w-xl">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="bg-carrot px-3 py-1.5 text-[0.64rem] font-extrabold uppercase tracking-[0.18em] text-ink">
                    Coming next
                  </span>
                  <span className="text-xs font-bold uppercase tracking-[0.18em] text-field-deep/80">Stripe Connect</span>
                </div>
                <h3 className="mt-6 text-4xl font-extrabold leading-[0.98] tracking-[-0.035em] text-field-deep sm:text-5xl">
                  Invoice the work while the work is fresh.
                </h3>
                <p className="mt-6 text-lg leading-relaxed text-ink/68">
                  Ask Carrot to prepare an invoice in plain language. Review the customer, service and amount,
                  then approve it. Carrot creates the invoice; Stripe handles the payment.
                </p>
                <p className="mt-5 border-l-2 border-carrot pl-5 font-semibold leading-relaxed text-field-deep">
                  Your Stripe account. Your customer. Your approval. Your money stays under your control.
                </p>
                <p className="mt-4 text-sm leading-relaxed text-ink/75">
                  Pricing stays explicit: your Carrot subscription and any disclosed processing charge. No
                  hidden platform fee or surprise take rate.
                </p>
              </div>

              <div className="invoice-demo-shell">
                <div className="invoice-demo-core">
                  <div className="flex items-center justify-between gap-4">
                    <span className="text-[0.64rem] font-bold uppercase tracking-[0.2em] text-ink/70">Staged example</span>
                    <span className="inline-flex items-center gap-2 text-xs font-bold text-field-deep">
                      <span className="size-2 rounded-full bg-field" /> Draft only
                    </span>
                  </div>

                  {/* the demo acts out its own steps on reveal:
                      you ask -> Carrot drafts -> you approve -> Stripe sends */}
                  <blockquote className="invoice-ask mt-7 rounded-[1.35rem] rounded-tr-md bg-field-deep px-5 py-4 text-lg font-medium leading-relaxed text-cream sm:ml-10">
                    “Send Regina a $145 invoice for today's training session.”
                  </blockquote>

                  <div className="invoice-draft mt-5 rounded-[1.35rem] bg-paper p-5 shadow-[inset_0_1px_0_rgba(255,255,255,0.8)] sm:p-6">
                    <div className="flex items-start justify-between gap-5 border-b border-ink/10 pb-5">
                      <div>
                        <span className="text-xs font-bold uppercase tracking-[0.18em] text-carrot-ink">Invoice preview</span>
                        <h4 className="mt-2 text-2xl font-extrabold text-ink">Training session</h4>
                        <p className="mt-1 text-sm text-ink/70">To Regina · due on receipt</p>
                      </div>
                      <strong className="font-display text-3xl text-field-deep">$145</strong>
                    </div>
                    <div className="mt-5 flex items-center justify-between gap-4">
                      <span className="text-sm font-semibold text-ink/70">Nothing sends without approval</span>
                      <span className="invoice-approve rounded-full bg-carrot-action px-4 py-2 text-sm font-extrabold text-white">Review & send</span>
                    </div>
                  </div>

                  <ol className="invoice-steps mt-6 grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-4">
                    {INVOICE_STEPS.map(([number, label]) => (
                      <li key={number} className="flex items-center gap-2 text-xs font-bold text-ink/70">
                        <span className="font-mono text-carrot-ink">{number}</span>
                        {label}
                      </li>
                    ))}
                  </ol>
                </div>
              </div>
            </div>
          </section>
        </Reveal>

      </div>

      <div className="mt-16 sm:mt-20">
        <CallToAction />
      </div>
    </section>
  );
}
